import React, { createContext, useContext, useState } from 'react';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import ModalFormRepository from '@/finsight/infrastructure/repository/ModalFormRepository';
import { useDIContext } from '@/Framework/DI/DIContext';
import { useRouter } from 'next/router';
import type IDealCellData from '@/finsight/ui/components/deals/DealListTable/cells/types';

interface ICorrectionData {
  comments: string,
  email: string,
  recaptcha: string,
}

const useCorrectionModal = () => {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isSubmitSucceed, setIsSubmitSucceed] = useState<boolean>(false);
  const [selectedDeal, setSelectedDeal] = useState<IDealCellData | null>(null);
  const { container } = useDIContext();
  const { asPath } = useRouter();

  const resetCorrectionForm = () => {
    setSelectedDeal(null);
    setIsSubmitSucceed(false);
  };

  async function sendCorrectionData(dealId: string, file: File, values: ICorrectionData) {
    setIsFetching(true);
    try {
      const payload = {
        correctionsText: values.comments,
        email: values.email || '',
        referrer: asPath,
        token: values.recaptcha,
        file,
      };
      const modalFormRepository = container.get<ModalFormRepository>(ModalFormRepository);
      await modalFormRepository.sendCorrectionModalForm(payload, dealId);
      setIsFetching(true);
      setIsSubmitSucceed(true);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      setIsSubmitSucceed(false);
    } finally {
      setIsFetching(false);
    }
  }

  return {
    selectedDeal,
    isFetching,
    isSubmitSucceed,
    setSelectedDeal,
    sendCorrectionData,
    resetCorrectionForm,
  };
};

type CorrectionModalContextType = ReturnType<typeof useCorrectionModal>;

export const CorrectionModalContext = createContext<CorrectionModalContextType>(null);

export const useCorrectionModalContext = (): CorrectionModalContextType => {
  const context = useContext<CorrectionModalContextType>(CorrectionModalContext);
  if (!context) {
    throw new Error('useCorrectionModalContext must be used within a CorrectionModalContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const CorrectionModalProvider = ({ children }: IProps) => (
  <CorrectionModalContext.Provider value={ useCorrectionModal() }>
    { children }
  </CorrectionModalContext.Provider>
);

export default CorrectionModalProvider;
